import React, { FC, useContext, useEffect, useState } from "react";
import clsx from "clsx";
import { IonModal, IonTextarea } from "@ionic/react";

import { Context } from "../../../store/student";
import WordCounter from "../../../atoms/WordCounter";
import {
  IPulseSurveyAnswer,
  PulseSurveyQuestionWithAnswer,
} from "../../../state";
import { ReactComponent as CloseIcon } from "../../../assets/icons/close_no_circle.svg";
import { ReactComponent as WeatherIconRainy } from "../../../assets/images/PulseSurvey/weather_icon_rainy.svg";
import { ReactComponent as WeatherIconThunderstorm } from "../../../assets/images/PulseSurvey/weather_icon_thunderstorm.svg";
import { ReactComponent as WeatherIconCloudy } from "../../../assets/images/PulseSurvey/weather_icon_cloudy.svg";
import { ReactComponent as WeatherIconMostlyCloudy } from "../../../assets/images/PulseSurvey/weather_icon_mostly-cloudy.svg";
import { ReactComponent as WeatherIconPartlyCloudy } from "../../../assets/images/PulseSurvey/weather_icon_partly-cloudy.svg";
import { ReactComponent as WeatherIconSunny } from "../../../assets/images/PulseSurvey/weather_icon_sunny.svg";

import styles from "./Answer.module.scss";

export type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSubmitAnswers: (answers: IPulseSurveyAnswer) => void;
  editData?: PulseSurveyQuestionWithAnswer | null;
};

const Answer: FC<Props> = ({
  isOpen,
  onClose,
  onSubmitAnswers,
  editData = null,
}) => {
  const { contextState } = useContext(Context);
  const [answers, setAnswers] = useState<{
    score: number | null;
    reason: string | null;
    tomorrow_plan: string | null;
  }>({
    score: null,
    reason: null,
    tomorrow_plan: null,
  });

  useEffect(() => {
    if (editData) {
      setAnswers({
        score: editData.answer.pulse_survey_question_score,
        reason: editData.answer.pulse_survey_question_reason,
        tomorrow_plan: editData.answer.tomorrow_plan,
      });
    } else {
      setAnswers({
        score: null,
        reason: null,
        tomorrow_plan: null,
      });
    }
  }, [editData, isOpen]);

  const weatherIcons = [
    <WeatherIconRainy />,
    <WeatherIconThunderstorm />,
    <WeatherIconCloudy />,
    <WeatherIconMostlyCloudy />,
    <WeatherIconPartlyCloudy />,
    <WeatherIconSunny />,
  ];
  const isAnyAnswered =
    answers.score !== null ||
    answers.reason !== null ||
    answers.tomorrow_plan !== null;

  const questionContent = editData
    ? editData.content
    : contextState.pulse_survey_question.content;

  const questionId = editData
    ? editData.id
    : contextState.pulse_survey_question.id;

  const maxScore = editData
    ? editData.max_score
    : contextState.pulse_survey_question.max_score;

  return (
    <IonModal isOpen={isOpen} className={styles.modal}>
      <div className={styles.overlay} />
      <div className={styles.wrapper}>
        <div className={styles.content}>
          <CloseIcon className={styles.closeIcon} onClick={onClose} />
          <div className={styles.inner}>
            <p className={styles.title}>
              {editData ? "デイリーストックを編集" : "1日を振り返ろう。"}
            </p>
            <div className={styles.scores}>
              {[...Array(maxScore)].map((_, index) => {
                const score = index + 1;
                const icon = weatherIcons[index];
                return (
                  <div key={score} className={styles.scoreList}>
                    <div className={styles.weatherItem}>{icon}</div>
                    <div className={styles.radioItem}>
                      <label className={styles.radioButton}>
                        <input
                          className={styles.radioInput}
                          type="radio"
                          name="score"
                          value={score}
                          checked={score === answers.score}
                          onChange={() =>
                            setAnswers(prev => ({
                              ...prev,
                              score,
                            }))
                          }
                        />
                        <span className={styles.radioCustom} />
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className={styles.question}>{questionContent}</p>
            <IonTextarea
              className={clsx(styles.textInput, styles.reason)}
              placeholder="どうしてそう感じたか、具体的に書いてみよう。"
              value={answers.reason ?? ""}
              maxlength={500}
              rows={1}
              onIonChange={e => {
                const value = e.detail.value ?? "";
                setAnswers(prev => ({
                  ...prev,
                  reason: value.trim() === "" ? null : value,
                }));
              }}
            />
            <div className={styles.counter}>
              <WordCounter
                maxLength={500}
                targetLength={answers.reason?.length ?? 0}
              />
            </div>

            <IonTextarea
              className={clsx(styles.textInput, styles.tomorrowPlan)}
              placeholder="明日はどんな1日にしたい？"
              value={answers.tomorrow_plan ?? ""}
              maxlength={500}
              rows={1}
              onIonChange={e => {
                const value = e.detail.value ?? "";
                setAnswers(prev => ({
                  ...prev,
                  tomorrow_plan: value.trim() === "" ? null : value,
                }));
              }}
            />
            <div className={styles.counter}>
              <WordCounter
                maxLength={500}
                targetLength={answers.tomorrow_plan?.length ?? 0}
              />
            </div>
            <button
              className={clsx(styles.saveButton)}
              type="button"
              disabled={!isAnyAnswered}
              onClick={() =>
                onSubmitAnswers({
                  pulse_survey_question_id: questionId,
                  ...answers,
                })
              }
            >
              保存
            </button>
          </div>
        </div>
      </div>
    </IonModal>
  );
};

export default Answer;
